import { Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { CdkDragEnd, DragRef, Point } from '@angular/cdk/drag-drop';
import { ActionMonitorService } from '../../services/action-monitor.service';
import { ActionMonitorRun, isXactChatRun } from '../../models/action-monitor.model';
import { XactChatRunService } from '../../services/xact-chat-run.service';
import { SecondaryCursorService } from '../../services/secondary-cursor.service';
import { MissionRunTabService } from '../../services/mission-run-tab.service';
import { isMissionRoute } from '../../mission-route';

/** How close to the viewport's edge the window may sit — the floating Xact Playground's margin. */
const VIEWPORT_MARGIN_PX = 8;

/**
 * Where an undragged window sits: ONE object, never a fresh literal. `cdkDragFreeDragPosition` is an input, and a new
 * `{ x: 0, y: 0 }` on every change-detection pass is a new value to it — the CDK put the window back at the origin
 * mid-drag, which a streaming chat (a check on every chunk) made happen constantly (review, 2026-09-27).
 */
const DRAG_ORIGIN: Readonly<{ x: number; y: number }> = Object.freeze({ x: 0, y: 0 });

/**
 * THE ACTION MONITOR (XAT-E-253-01 — was `WorkflowExecutionFloatComponent`).
 *
 * Mounted ONCE at the app shell, beside the router outlet rather than inside it: the run it is
 * watching navigates this tab, so the page that started it is gone long before the last step
 * finishes.
 *
 * It renders whatever run the service is holding and knows nothing about where the run came from.
 *
 * It defaults to the PILL. A run raising a panel over the UI it is about to drive is exactly the
 * wrong default, and the developer's own choice is remembered by the service for the session.
 *
 * THE PANEL'S BODY IS THE RUN VIEW (XCT-E-12-05) — `<app-action-monitor-run-view>`, the rows, Stop, Re-run and
 * Copy log, shared with the floating Xact Playground, which shows the runs IT started under its script. For those,
 * while that window is up, it is the run's host and this component draws nothing (`actionMonitorHostFor`).
 *
 * THE RUN WINDOW EVERY USER GETS (XAT-E-377-05). The script editor is the developer's; a run's log, and the
 * conversation a chat's run came from, are everyone's — so this is where every other run is shown, mounted again at
 * the app shell. A chat's run opens on its log, with its conversation a tab away (`app-run-thread-chat`, live, with its
 * composer); any other run shows its log alone.
 */
@Component({
  standalone: false,
  selector: 'app-action-monitor',
  templateUrl: './action-monitor.component.html',
  styleUrls: ['./action-monitor.component.scss']
})
export class ActionMonitorComponent implements OnInit, OnDestroy {
  run: ActionMonitorRun | null = null;
  collapsed = true;

  /** The floating Xact Playground is showing the run instead — one it started (XCT-E-12-05, XAT-E-377-05). */
  hostedElsewhere = false;

  /** A chat's run (XAT-E-377): which tab the window shows — the run's log, or the conversation it came from. */
  runTab: 'chat' | 'log' = 'log';

  /** The window made large — the floating XatBot window's expanded height (XAT-E-377-02). */
  isLarge = false;

  private destroy$ = new Subject<void>();

  /** The mission this tab left to run a screen script (MISN-E-224-06): the way back is a click away while it is away. */
  private readonly missionRunTab = inject(MissionRunTabService, { optional: true });

  constructor(
    private monitor: ActionMonitorService,
    private xactChatRunService: XactChatRunService,
    private secondaryCursorService: SecondaryCursorService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.monitor.run$.pipe(takeUntil(this.destroy$)).subscribe(run => {
      // A NEW RUN OPENS ON ITS LOG (review, 2026-09-27): what the person is watching is the run.
      if (run?.status === 'running' && run.runId !== this.run?.runId) this.runTab = 'log';
      const grew = !!run?.chatThreadId !== !!this.run?.chatThreadId;
      this.run = run;
      // A chat's run makes the window taller: pulled back on screen once it is drawn at that size.
      if (grew) this.keepInViewSoon();
    });
    this.monitor.collapsed$.pipe(takeUntil(this.destroy$)).subscribe(c => {
      this.collapsed = c;
      if (!c) this.keepInViewSoon();
    });
    this.monitor.host$.pipe(takeUntil(this.destroy$)).subscribe(host => (this.hostedElsewhere = host !== 'monitor'));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Chrome ─────────────────────────────────────────────────────────────────────────────────────

  minimize(): void { this.monitor.setCollapsed(true); }
  expandPanel(): void { this.monitor.setCollapsed(false); }
  /**
   * XAT-E-618-16 — the run overlay has no Stop of its own any more (XAT-E-618-15), so closing the monitor while it covers
   * the page would leave the page blocked with no way to stop the run. Close stops the run first, then closes.
   */
  close(): void {
    if (this.secondaryCursorService.isRunOverlayActive()) this.secondaryCursorService.requestStop();
    this.monitor.close();
  }
  toggleSize(): void {
    this.isLarge = !this.isLarge;
    this.keepInViewSoon();
  }

  // ── The run thread (XAT-E-377) ─────────────────────────────────────────────────────────────────

  /**
   * A RUN STARTED FROM CHAT (XAT-E-526-03), told by its key: shown in the reader's view — plain numbered steps — in either
   * branch of the window, for everyone, staff included.
   */
  get isChatRun(): boolean { return isXactChatRun(this.run); }

  /** The run came from a chat: it has a thread to show or to open. */
  get hasRunThread(): boolean { return !!this.run?.chatThreadId; }

  /** The window shows that thread as a chat — unless its agent reads its page's state (`chatBindingFor`). */
  get showsRunChat(): boolean { return this.hasRunThread && this.run?.chatBinding !== 'open-chat'; }

  /** The agent the thread belongs to, so a reply in the window reaches it. */
  get runChatAgent(): string | null {
    const binding = this.run?.chatBinding;
    return binding && binding !== 'open-chat' ? binding.agentType : null;
  }

  showRunTab(tab: 'chat' | 'log'): void { this.runTab = tab; }

  // ── The dock tab (review, 2026-09-27) ───────────────────────────────────────────────────────────

  /** Open the window on one of its tabs — the dock tab opens it on the Execution log. */
  openOn(tab: 'chat' | 'log'): void {
    this.runTab = tab === 'chat' && this.showsRunChat ? 'chat' : 'log';
    this.expandPanel();
  }

  /** The Execution tab's heading: what the run is doing, in a reader's words. */
  get execTitle(): string {
    switch (this.run?.status) {
      case 'running': return 'Working on it';
      case 'completed': return 'Done';
      case 'failed': return 'Something went wrong';
      case 'timed-out': return 'Timed out';
      default: return 'Stopped';
    }
  }

  /** Under it: the step it is on while it runs, then the run's own outcome. */
  get execSubtitle(): string {
    const run = this.run;
    if (!run) return '';
    if (run.status === 'running') {
      return run.total ? `Step ${Math.min(run.done + 1, run.total)} of ${run.total}` : 'Starting…';
    }
    return run.outcome || this.statusLabel;
  }

  get execPercent(): number {
    const run = this.run;
    if (!run?.total) return run && run.status !== 'running' ? 100 : 0;
    return Math.round((Math.min(run.done, run.total) / run.total) * 100);
  }

  /** The run's state in words: going, then how it went. */
  get dockStatus(): string {
    if (this.isRunning) return 'Running…';
    return this.run?.outcome || this.statusLabel;
  }

  /**
   * Stop from the tab: an Xact run only, through the abort path the window's Stop uses (XAT-E-253-03). A workflow's
   * Stop reaches its thread and its answer as well, so it stays in the window.
   */
  get canStopFromDock(): boolean {
    return this.isRunning && !!this.run?.canStop && this.run?.source === 'xact';
  }

  stopRun(): void {
    if (this.canStopFromDock) this.secondaryCursorService.requestStop();
  }

  /**
   * CONTINUE THE CONVERSATION IN THE XATBOT PANEL, AND ONLY THERE (XAT-E-377-04, review 2026-09-27). Two live XatBots on
   * one thread fight over the screen, so the window's chat goes first — the window folds to its pill while the run is
   * going (the pill draws no chat) and is put away once it has finished — and the panel opens on the next tick.
   */
  continueInXatBot(): void {
    const threadId = this.run?.chatThreadId;
    if (this.isRunning) this.minimize(); else this.close();
    setTimeout(() => this.xactChatRunService.openRunThreadInXatBot(threadId));
  }

  /**
   * This tab left its mission for a screen run, the run is over and the person is not back yet (MISN-E-224-06). Not while the run goes:
   * its remaining steps would act on the mission page; Stop is the way out of a run that is still going.
   */
  get awayFromMission(): boolean {
    return !!this.missionRunTab?.away && !this.isRunning && !isMissionRoute(window.location.pathname);
  }

  /** **Back to the mission**, one click (MISN-E-224-06). */
  backToMission(): void { this.missionRunTab?.backToMission(); }

  /** A thread the window cannot host (its agent reads its page): continue it in the XatBot panel. */
  openRunChat(): void { this.xactChatRunService.openRunThreadInXatBot(this.run?.chatThreadId); }

  /**
   * THE DRAG STAYS ON SCREEN, ON THE SAME TERMS AS keepInView (review, 2026-09-27). Not `cdkDragBoundary`: the page's
   * content area is shorter than the viewport keepInView allows, so a window placed over the top bar, or expanded
   * taller than that area, was pinned by the boundary and would not move. The whole window stays inside the viewport
   * margin — the top and left edges win when it is bigger than the screen.
   *
   * IT ANSWERS WITH THE WINDOW'S TOP-LEFT, not the pointer: with a constrain function the CDK places the element's
   * corner at the returned point (DragRef `_getConstrainedPointerPosition`, "the pickup position has already been
   * accounted for"). Returning the pointer dropped the corner under the cursor — a jump by the grab offset on every
   * drag, and another at the drop when keepInView pulled the overshoot back.
   */
  readonly keepDragInView = (pointer: Point, _drag: DragRef, rect: DOMRect, pickup: Point): Point => {
    const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));
    return {
      x: clamp(pointer.x - pickup.x, VIEWPORT_MARGIN_PX, window.innerWidth - VIEWPORT_MARGIN_PX - rect.width),
      y: clamp(pointer.y - pickup.y, VIEWPORT_MARGIN_PX, window.innerHeight - VIEWPORT_MARGIN_PX - rect.height)
    };
  };

  onDragEnded(event: CdkDragEnd): void {
    this.monitor.position = event.source.getFreeDragPosition();
    this.keepInView();
  }

  /**
   * NEVER OFF THE SCREEN, ON ANY SIDE (review, 2026-09-27) — the floating Xact Playground's rule, for the same reason.
   * cdkDrag keeps a drag inside the page, but a window dragged up and then expanded, or opened into a shorter browser,
   * reaches past the viewport with its title bar out of reach. So after every change of size or place, and on a
   * resize, the dropped offset is pulled back just far enough for the whole window to show — top edge first, so the bar
   * with its controls is always reachable.
   */
  @HostListener('window:resize')
  keepInView(): void {
    const element = this.host.nativeElement.querySelector<HTMLElement>('#action-monitor');
    if (!element || this.collapsed) return;

    const rect = element.getBoundingClientRect();
    const position = this.dragPosition;
    let dx = 0;
    let dy = 0;

    if (rect.bottom > window.innerHeight - VIEWPORT_MARGIN_PX) dy = window.innerHeight - VIEWPORT_MARGIN_PX - rect.bottom;
    if (rect.top + dy < VIEWPORT_MARGIN_PX) dy = VIEWPORT_MARGIN_PX - rect.top;
    if (rect.right > window.innerWidth - VIEWPORT_MARGIN_PX) dx = window.innerWidth - VIEWPORT_MARGIN_PX - rect.right;
    if (rect.left + dx < VIEWPORT_MARGIN_PX) dx = VIEWPORT_MARGIN_PX - rect.left;

    if (dx || dy) this.monitor.position = { x: position.x + dx, y: position.y + dy };
  }

  /** After the render the change causes — the window's size is only known once it is drawn. */
  private keepInViewSoon(): void {
    setTimeout(() => this.keepInView());
  }

  get dragPosition(): { x: number; y: number } {
    return this.monitor.position ?? DRAG_ORIGIN;
  }

  // ── What the pill reads ────────────────────────────────────────────────────────────────────────

  get isRunning(): boolean { return this.run?.status === 'running'; }

  get counter(): string { return `${this.run?.done ?? 0} / ${this.run?.total ?? 0}`; }

  /**
   * THE RUN'S STATE AS A WORD (XAT-E-265-03) — what the state dot has always said in colour. Collapsed, that is
   * the whole of what the pill says, since the label never changes and the counter is only a fraction.
   * `timed-out` reads as "timed out": the tooltip is a sentence to a reader, not a value to a caller.
   */
  get statusLabel(): string {
    switch (this.run?.status) {
      case 'running':
        return 'running';
      case 'completed':
        return 'completed';
      case 'failed':
        return 'failed';
      case 'stopped':
        return 'stopped';
      case 'timed-out':
        return 'timed out';
      default:
        return '';
    }
  }
}
